import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';

import { Rule, RulesMap } from '../../types/Rules';
import { useVoucher } from '../../context/VoucherProdiver';
import { getLineById } from '../../apis';
import { getCachedBrands } from '../VoucherForm/Rules/Brands/brandsCache';
import { Line } from '../../types/Line';
import { Brand } from '../../types/Brand';
import { deriveCreationTimezoneOffset } from '../../helpers/dateTime';
import { useRuleEntities } from './useRuleEntities';

import InfoDisplay from './InfoDisplay';
import { Currency } from '@flixbus/honeycomb-react';
import styles from './voucher-info.module.scss';

const isUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

const LINE_RULE_TYPES: Rule['type'][] = ['line_blacklist', 'line_whitelist'];
const BRAND_RULE_TYPES: Rule['type'][] = ['brand_allowlist', 'brand_disallowlist'];

export interface RuleDisplayProps {
  title: string;
  fields: Rule['type'][];
}

const RuleDisplay = ({ title, fields }: RuleDisplayProps) => {
  const {
    data: { rules },
  } = useVoucher();

  const getLineValues = useCallback(
    (rule: Rule) => ('lines' in rule ? (rule as Rule & { lines: string[] }).lines : []),
    [],
  );

  const fetchLines = useCallback(
    (uuids: string[]) => Promise.all(uuids.map(id => getLineById(id))),
    [],
  );

  const getLineKey = useCallback((line: Line) => line.lineId, []);

  const getBrandValues = useCallback(
    (rule: Rule) => ('brands' in rule ? (rule as Rule & { brands: string[] }).brands : []),
    [],
  );

  const fetchBrands = useCallback(
    (uuids: string[]) =>
      getCachedBrands().then(allBrands => allBrands.filter(b => uuids.includes(b.uuid))),
    [],
  );

  const getBrandKey = useCallback((brand: Brand) => brand.uuid, []);

  const { entityMap: linesMap, loading: loadingLines } = useRuleEntities<Line>({
    rules,
    fields,
    ruleTypes: LINE_RULE_TYPES,
    getValues: getLineValues,
    fetchEntities: fetchLines,
    getKey: getLineKey,
  });

  const { entityMap: brandsMap, loading: loadingBrands } = useRuleEntities<Brand>({
    rules,
    fields,
    ruleTypes: BRAND_RULE_TYPES,
    getValues: getBrandValues,
    fetchEntities: fetchBrands,
    getKey: getBrandKey,
  });

  const renderDate = useMemo(
    () =>
      (str: string, isEndOfDay = false) => {
        const { offsetString, offsetMinutes } = deriveCreationTimezoneOffset(str, isEndOfDay);
        const dt = DateTime.fromISO(str, { zone: 'utc' }).plus({ minutes: offsetMinutes });
        return `${dt.toFormat('dd.MM.yyyy')}, ${dt.toFormat('HH:mm')} ${offsetString}`;
      },
    [],
  );

  if (!rules || rules.length === 0) return null;

  const renderRuleValue = (
    ruleType: Rule['type'],
    fieldKey?: 'start' | 'end',
    ruleIndex?: number,
  ) => {
    const matchingRules = rules.filter(r => r.type === ruleType);

    const rule =
      ruleType === 'blackout_period' && ruleIndex !== undefined
        ? matchingRules[ruleIndex]
        : matchingRules[0];

    if (!rule) return '-';

    switch (rule.type) {
      case 'allowed_countries':
        return rule.countries.join(', ');
      case 'forbidden_countries':
        return rule.countries.join(', ');
      case 'allowed_platforms':
        return rule.platforms.join(', ');
      case 'travel_period':
      case 'blackout_period':
        if (fieldKey === 'start') {
          return rule.start ? renderDate(rule.start, false) : 'N/A';
        } else if (fieldKey === 'end') {
          return rule.end ? renderDate(rule.end, true) : 'N/A';
        }
        return `${rule.start ? renderDate(rule.start, false) : 'N/A'} to ${rule.end ? renderDate(rule.end, true) : 'N/A'}`;
      case 'direct_ride':
        return 'Direct Ride Only';
      case 'line_blacklist':
      case 'line_whitelist':
        if (loadingLines) return 'Loading...';
        return rule.lines
          .map(lineValue => {
            if (isUUID(lineValue)) {
              const line = linesMap[lineValue];
              return line ? `${line.code} - ${line.title}` : lineValue;
            }
            return lineValue;
          })
          .join(', ');
      case 'brand_allowlist':
      case 'brand_disallowlist':
        if (loadingBrands) return 'Loading...';
        return rule.brands
          .map(brandUuid => {
            const brand = brandsMap[brandUuid];
            return brand ? brand.name : brandUuid;
          })
          .join(', ');
      case 'minimum_tickets_amount':
        return `${rule.count}`;
      case 'minimum_value':
        return <Currency value={rule.value.amount} code={rule.value.currency} />;
      case 'offline_sales':
        return 'Offline Sales Only';
      case 'weekdays_travel':
        return rule.weekDays.join(', ');
      case 'free_ancillaries':
        return rule.ancillaries.join(', ');
      default:
        return '-';
    }
  };

  const expandedFields = fields.flatMap(f => {
    if (f === 'travel_period') {
      return [
        { label: `${RulesMap[f]} since`, value: renderRuleValue(f, 'start') },
        { label: `${RulesMap[f]} until`, value: renderRuleValue(f, 'end') },
      ];
    }
    if (f === 'blackout_period') {
      const blackoutRules = rules.filter(r => r.type === 'blackout_period');

      return blackoutRules.map((_, index) => ({
        label: '',
        value: (
          <>
            <span className={styles.label}>From</span>{' '}
            <strong>{renderRuleValue(f, 'start', index)}</strong>{' '}
            <span className={styles.label}>To</span>{' '}
            <strong>{renderRuleValue(f, 'end', index)}</strong>
          </>
        ),
        skipBoldWrapper: true,
      }));
    }
    return [{ label: RulesMap[f], value: renderRuleValue(f) }];
  });

  return <InfoDisplay title={title} fields={expandedFields} />;
};

export default RuleDisplay;
